/**
 * Better Auth browser client (spec §13: "Auth — Better Auth at /api/auth/*").
 *
 * The API mounts Better Auth's catch-all at `/api/auth`, so the browser talks
 * to the same origin and the session cookie is SameSite=Lax; there is no token
 * to store and nothing about the session lives in JavaScript.
 *
 * Better Auth answers with `{data, error}` instead of throwing. The wrappers
 * here turn a failure into a thrown `Error` carrying the server's message, so
 * a form component can `try/catch` like it does around every other call.
 */
import { createAuthClient } from "better-auth/react";
import { ELIGIBILITY_HEADER, ELIGIBILITY_HEADER_VALUE } from "@madaline/shared/legal";
import { ApiError } from "./api-client.js";

/** §13's mount point. */
export const AUTH_BASE_PATH = "/api/auth";

/**
 * The web route a "confirm your email" link redirects to. The API mints
 * resent links with the same path (`apps/api/src/mail/verification.ts`).
 */
export const VERIFY_EMAIL_PATH = "/verify-email";

export const authClient = createAuthClient({
  baseURL: typeof window === "undefined" ? "http://localhost" : window.location.origin,
  basePath: AUTH_BASE_PATH,
});

export interface PasswordCredentials {
  email: string;
  password: string;
}

export interface SignUpInput extends PasswordCredentials {
  name: string;
  /**
   * An invitation code, when the deployment admits accounts by invitation
   * (`GET /auth/config` → `signup.mode: "invite"`). It travels as the
   * `x-madaline-invite` HEADER, so the body Better Auth sees is untouched.
   */
  invite?: string;
  /**
   * The Terms of service and the Privacy policy the form showed, at their
   * versions. The server records them with the account, and refuses a
   * version that is no longer in force (`409 AGREEMENT_VERSION_SUPERSEDED`).
   * A header, like the invitation.
   */
  agreements?: ReadonlyArray<{ doc_key: string; version: string }>;
  /**
   * The person ticked the Terms' eligibility confirmation
   * (`SIGNUP_ELIGIBILITY_STATEMENT`): of age, using Madaline for their own
   * trading only, not on a sanctions list. Sent as `x-madaline-eligibility:
   * adult,individual,not_sanctioned` (`ELIGIBILITY_HEADER_VALUE`), recorded
   * with the agreements; a sign-up that names its agreements without it is
   * refused.
   */
  eligibility?: boolean;
}

/** The header an invitation code travels in (`signup.invite_header`). */
export const INVITE_HEADER = "x-madaline-invite";

/** The header the agreements the form showed travel in (`plugins/disclosures.ts`). */
export const AGREEMENTS_HEADER = "x-madaline-agreements";

/**
 * What the password half of a sign-in answers for an account with a second
 * factor (KNOWN-DEBT D5): the browser now holds a sign-in that can do nothing
 * but answer `POST /api/v1/auth/sign-in/second-factor`, until `expires_at`.
 */
export interface SignInChallenge {
  methods: string[];
  expires_at: string;
}

/** The challenge out of a sign-in answer, or null when the answer signed the browser in. */
export function challengeOf(data: unknown): SignInChallenge | null {
  if (data === null || typeof data !== "object") return null;
  const body = data as { two_factor_required?: unknown; twoFactorRedirect?: unknown; methods?: unknown; expires_at?: unknown };
  if (body.two_factor_required !== true && body.twoFactorRedirect !== true) return null;
  const methods = Array.isArray(body.methods)
    ? body.methods.filter((m): m is string => typeof m === "string")
    : ["totp", "recovery_code"];
  const expires =
    typeof body.expires_at === "string" && !Number.isNaN(Date.parse(body.expires_at))
      ? body.expires_at
      : // The server always sends it; ten minutes is its window if it ever did not.
        new Date(Date.now() + 10 * 60_000).toISOString();
  return { methods, expires_at: expires };
}

interface BetterAuthResult {
  error?: {
    message?: string | undefined;
    status?: number;
    code?: string | undefined;
    details?: unknown;
  } | null;
}

/**
 * A refusal keeps its status, its code and its details. The sign-in and
 * sign-up screens tell "wrong password" from "too many attempts, try again in
 * 42 seconds" from "sign-ups are closed" by `code`, never by the wording,
 * which the server may change; and a 429 carries `retry_after_seconds`.
 */
function raise(result: BetterAuthResult, fallback: string): void {
  const error = result.error;
  if (!error) return;
  const status = typeof error.status === "number" ? error.status : 0;
  throw new ApiError({
    status,
    code: typeof error.code === "string" && error.code !== "" ? error.code : `HTTP_${status}`,
    message: error.message || fallback,
    details: error.details,
  });
}

/**
 * The password half of signing in. Resolves with nothing when that is the
 * whole of it, and with the challenge when the account has a second factor —
 * in which case the cookie this response set opens nothing but the challenge.
 */
export async function signInWithPassword(credentials: PasswordCredentials): Promise<SignInChallenge | void> {
  const result = await authClient.signIn.email({
    email: credentials.email,
    password: credentials.password,
  });
  raise(result, "Could not sign in. Check your email and password.");
  const challenge = challengeOf((result as { data?: unknown }).data);
  if (challenge) return challenge;
}

export async function signUpWithPassword(input: SignUpInput): Promise<void> {
  const invite = input.invite?.trim();
  const headers: Record<string, string> = {};
  if (invite) headers[INVITE_HEADER] = invite;
  if (input.agreements && input.agreements.length > 0) {
    headers[AGREEMENTS_HEADER] = input.agreements.map((a) => `${a.doc_key}@${a.version}`).join(",");
  }
  if (input.eligibility === true) headers[ELIGIBILITY_HEADER] = ELIGIBILITY_HEADER_VALUE;
  const result = await authClient.signUp.email(
    {
      email: input.email,
      password: input.password,
      name: input.name,
      // Where the confirmation link in the sign-up mail lands. Better Auth's
      // default is `/` — the marketing page, which acknowledges nothing and
      // ignores the `?error=` an expired link arrives with. `/verify-email` is
      // the page that says which of the two happened.
      callbackURL: VERIFY_EMAIL_PATH,
    },
    Object.keys(headers).length > 0 ? { headers } : undefined,
  );
  raise(result, "Could not create the account.");
}

/**
 * What the button says: EVERY session of this account, on every device.
 *
 * This used to call `signOut()` alone, which ends the session it is called
 * from and no other — measured against the API on 2026-10-13: a second
 * browser stayed signed in after "Sign out everywhere". `revokeSessions`
 * (`POST /api/auth/revoke-sessions`) deletes every session row for the user;
 * `signOut` then clears this browser's cookie, and answers 200 whether or not
 * the session it names still exists.
 */
export async function signOutEverywhere(): Promise<void> {
  const revoked = await authClient.revokeSessions();
  raise(revoked, "Could not sign out of your other devices.");
  const result = await authClient.signOut();
  raise(result, "Could not sign out.");
}

/**
 * What the account menu's "Sign out" says: THIS browser, and no other. It was
 * routed through `signOutEverywhere` while that function only ever signed out
 * here; once it learned to revoke every session (S1), the menu item started
 * signing a person out of their phone too.
 */
export async function signOutHere(): Promise<void> {
  const result = await authClient.signOut();
  raise(result, "Could not sign out.");
}

/**
 * One signed-in session of this account, as `GET /api/auth/list-sessions`
 * answers it. `token` is what `revoke-session` takes and is a secret: it is
 * held in memory to make that call, and is never drawn, logged or used as a
 * React key — `id` is for that.
 */
export interface AuthSession {
  id: string;
  token: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  /**
   * A sign-in whose password was right and which is still waiting for its
   * second factor (KNOWN-DEBT D5). It can do nothing until it is answered.
   */
  secondFactorPending?: boolean;
}

/** Every session of this account, and which one is this browser. */
export async function listAuthSessions(): Promise<{ current: string | null; sessions: AuthSession[] }> {
  const [list, here] = await Promise.all([authClient.listSessions(), authClient.getSession()]);
  raise(list, "Could not read where this account is signed in.");
  // Better Auth's client turns date strings into `Date`s on the way in; the
  // screens read ISO strings, as everything else on the wire is.
  const iso = (value: unknown): string =>
    value instanceof Date ? value.toISOString() : typeof value === "string" ? value : "";
  const sessions = ((list.data ?? []) as unknown as Record<string, unknown>[]).map((row) => ({
    ...(row as unknown as AuthSession),
    createdAt: iso(row["createdAt"]),
    updatedAt: iso(row["updatedAt"]),
    expiresAt: iso(row["expiresAt"]),
  }));
  const current = (here.data as { session?: { id?: string } } | null)?.session?.id ?? null;
  return { current, sessions };
}

/** Ends one other session. The device it was on is signed out on its next request. */
export async function revokeAuthSession(token: string): Promise<void> {
  const result = await authClient.revokeSession({ token });
  raise(result, "Could not sign that session out.");
}

/** Ends every session but this one. */
export async function revokeOtherAuthSessions(): Promise<void> {
  const result = await authClient.revokeOtherSessions();
  raise(result, "Could not sign out of your other devices.");
}

/**
 * `POST /api/auth/change-password`. The server always ends every OTHER
 * session when a password changes (S1), and hands this browser a new cookie.
 */
export async function changeAccountPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const result = await authClient.changePassword({
    currentPassword: input.currentPassword,
    newPassword: input.newPassword,
    revokeOtherSessions: true,
  });
  raise(result, "Could not change the password.");
}
